import { LitElement, html } from 'lit';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { style } from './iconic-vin-inputs.style.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { validateVin } from '../directives/valid-vin.directive.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../shared/constants/app.constants.js';
import { VehicleService } from '../../../services/vehicle.service.js';
import { stratum } from '../../../../constants/stratum.js';
import { ApplicationService } from '../../../services/application.service.js';

class RefiIconicVinInputs extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicVinInputs');
    this.errorMessage = 'Please enter a valid VIN';
    this.vehicleLookupModel = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.vehicleLookupModel = VehicleService.vehicleLookupModel;
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties);
    stratum.publishTag(
      ApplicationService.isInAppDeclineDelayOn
        ? TagKey.VIEW_VEHICLE_VIN_LOOKUP_3
        : TagKey.VIEW_VEHICLE_VIN_LOOKUP
    );
  }

  static properties = {
    errorMessage: { type: String, attribute: false },
    vehicleLookupModel: { type: Object, reflect: true },
  };

  static styles = [style, fsTypography];

  validateVin(ev) {
    let valid = false;
    const vin = ev.target.value;
    if (!vin) {
      this.errorMessage =
        REFI_VEHICLE_LOAN_CONST.VIN_VALIDATION_ERRORS.MIN_LENGTH.MESSAGE;
      return false;
    }
    const validation = validateVin(vin);

    if (validation) {
      const validationResult = Object.keys(validation)[0];
      this.debug.log('validation check:', validationResult);
      for (const validationError of Object.entries(
        REFI_VEHICLE_LOAN_CONST.VIN_VALIDATION_ERRORS
      )) {
        if (validationResult === validationError[1].KEY) {
          this.errorMessage = validationError[1].MESSAGE;
        }
      }
    } else {
      valid = true;
      this.errorMessage = null;
    }

    return valid;
  }

  changed(name, ev) {
    const valid = this.validateVin(ev);
    this.vehicleLookupModel[name] = ev?.target?.value?.trim();

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name,
          isValid: valid,
        },
      })
    );
  }

  tagWhereModalVinOpen() {
    stratum.publishTag(TagKey.VEHICLE_VIN_LOOKUP_VIN_HELP_MODULE_2);
  }

  closeWhereVinModal() {
    this.debug.log('close where vin modal');
    this.shadowRoot.querySelector('fs-modal-dialog').closeModalDialog();
  }

  render() {
    this.debug.log('rendered');

    return html`
      <div class="vin-field">
        <fs-text-field
          class="includes-error-message"
          id="vin"
          name="vin"
          type="text"
          value="${this.vehicleLookupModel.vin}"
          pattern-mismatch-message="Please enter a valid VIN"
          value-missing-message="Please enter a valid VIN"
          maxLength="17"
          minLength="17"
          pattern="^[a-zA-Z0-9]+$"
          required
          data-e2e-ref="fldVehicleVIN"
          error-message="${this.errorMessage}"
          @change="${(e) => {
            e.target.value = e.target.value.toUpperCase();
            this.changed('vin', e);
          }}"
          @blur="${(e) => {
            e.target.value = e.target.value.toUpperCase();
            this.changed('vin', e);
          }}"
          helper-text="${REFI_VEHICLE_LOAN_CONST.ICONIC_VIN_HELPER_TEXT}"
          >VIN</fs-text-field
        >
      </div>
      <div class="where-vin-modal-container">
        <fs-modal-dialog accessible-title="" dismissible="" size="small">
          <fs-button
            data-e2e-ref="whereVinModal"
            variant="primary"
            type="button"
            compact=""
            ghost=""
            id="open"
            @click="${this.tagWhereModalVinOpen}"
            >Need help finding your VIN?
          </fs-button>
          <refi-heading slot="modal-dialog-header">
            Where to find your VIN
          </refi-heading>
          <section
            class="fs-body-text fs-body-text--normal"
            slot="modal-dialog-body"
          >
            <fs-body-text size="normal" variant="primary">
              <p>You can usually find your VIN in these places:</p>
              <ul>
                <li>On your insurance card.</li>
                <li>
                  At the base of the windshield where it meets the dashboard on
                  the driver’s side.
                </li>
                <li>On the door jamb on the driver’s side.</li>
                <li>On your vehicle’s title and registration.</li>
                <li>On the front of your vehicle’s engine block.</li>
              </ul>
            </fs-body-text>
          </section>
          <fs-modal-dialog-footer
            footer-layout-swap-breakpoint="medium"
            slot="modal-dialog-footer"
          >
            <fs-button
              data-e2e-ref="closeWhereVinModal"
              focus-initial=""
              variant="primary"
              type="button"
              @click="${this.closeWhereVinModal}"
            >
              Okay
            </fs-button>
          </fs-modal-dialog-footer>
        </fs-modal-dialog>
      </div>
    `;
  }
}

customElements.define('refi-iconic-vin-inputs', RefiIconicVinInputs);
